import { Grid, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconExclamationCircle, IconX } from '@tabler/icons-react';
import * as React from 'react';
import { useMemo } from 'react';
import { MantineTable, type MantineColumn } from '../../../../../react-commons/components/table/MantineTable';
import { getColorMantine } from '../../../../../react-commons/components/inputs/text-fields/types';
import { ValidationEntrySeverity } from '../../../../../react-commons/components/ValidationEntrySeverity';
import { ControlledInputs } from '../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { formatIdentifier } from '../../../../../react-commons/utils/utilFunctions';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import { useSystemInformationActions } from '../../../resource/services/systemInformationService';

export const ResourceDetailsComponent = ({ resourceType }: { resourceType: ResourceType }) => {
    const [filterSubType, setFilterSubType] = React.useState<string | null>(null);

    const systemInformationActions = useSystemInformationActions();

    const resourceProperties = useSystemInformationActions().useGetAllPropertyDetails(resourceType);
    const resourceValidationEntries = systemInformationActions.useGetValidationEntryDetails(resourceType);
    const subTypes = systemInformationActions.useGetResourceSubTypes(resourceType);

    const getDisplayNameOfProperty = (identifer: string) => {
        if (resourceProperties) {
            const props = resourceProperties[identifer];
            if (props?.displayName) {
                return props.displayName;
            }
        }
        return formatIdentifier(identifer);
    };

    const hasSubTypes = () => (subTypes.data?.length ?? 0) > 1;

    const getExpandedContent = (description?: string, condition?: string) => {
        return (
            <Grid>
                {condition !== undefined && (
                    <Grid.Col span={description ? 6 : 12}>
                        <ControlledInputs.MultilineString
                            value={condition}
                            label={'Condition'}
                            maxRows={5}
                            readOnly={true}
                            nullable={false}
                        />
                    </Grid.Col>
                )}
                {((description !== undefined && condition === undefined) || description) && (
                    <Grid.Col span={condition !== undefined ? 6 : 12}>
                        <ControlledInputs.MultilineString
                            value={description}
                            label={'Description'}
                            maxRows={5}
                            readOnly={true}
                            nullable={false}
                        />
                    </Grid.Col>
                )}
            </Grid>
        );
    };

    const getResourcePropertyRows = () => {
        if (!resourceProperties) {
            return [] as Array<{
                displayName: string;
                functional: boolean;
                updatable: boolean;
                description?: string;
                condition?: string;
            }>;
        }
        return (
            Object.keys(resourceProperties)
                .filter((key) => resourceProperties[key].isBusinessProperty)
                .filter((key) =>
                    filterSubType ?
                        (resourceProperties[key].persistableTypeFilter?.includes(filterSubType) ?? true)
                        :   true
                )
                .map((key) => ({
                    displayName: getDisplayNameOfProperty(key),
                    functional: Boolean(resourceProperties[key].isFunctional),
                    updatable: Boolean(resourceProperties[key].updatable),
                    description: resourceProperties[key].description ?? undefined,
                    condition: undefined as string | undefined,
                })) ?? []
        );
    };

    const getValidationEntryRows = () => {
        if (resourceValidationEntries.data) {
            type Row = {
                severity: ValidationEntrySeverity | undefined;
                type: string;
                property: string;
                displayName: string;
                description?: string;
                condition?: string;
            };
            const propertyRows: Array<Row> =
                Object.keys(resourceValidationEntries.data.property)
                    .filter((key) =>
                        filterSubType ?
                            (resourceValidationEntries.data?.property[key].persistableTypeFilter?.includes(
                                filterSubType
                            ) ?? true)
                            :   true
                    )
                    .map((key) => ({
                        severity: resourceValidationEntries.data?.property[key].defaultSeverity,
                        type: 'Property',
                        property: getDisplayNameOfProperty(
                            resourceValidationEntries.data?.property[key].property ?? ''
                        ),
                        displayName: resourceValidationEntries.data?.property[key].displayName ?? formatIdentifier(key),
                        description: resourceValidationEntries.data?.property[key].description ?? undefined,
                        condition: resourceValidationEntries.data?.property[key].condition ?? '',
                    })) ?? [];
            const compoundRows: Array<Row> =
                Object.keys(resourceValidationEntries.data.compound)
                    .filter((key) =>
                        filterSubType ?
                            (resourceValidationEntries.data?.compound[key].persistableTypeFilter?.includes(
                                filterSubType
                            ) ?? true)
                            :   true
                    )
                    .map((key) => ({
                        severity: resourceValidationEntries.data?.compound[key].defaultSeverity,
                        type: 'Compound',
                        property:
                            resourceValidationEntries.data?.compound[key].properties ?
                                (resourceValidationEntries.data.compound[key].properties
                                    ?.map((value) => getDisplayNameOfProperty(value))
                                    .join(', ') ?? '')
                                :   '-',
                        displayName: resourceValidationEntries.data?.compound[key].displayName ?? formatIdentifier(key),
                        description: resourceValidationEntries.data?.compound[key].description ?? undefined,
                        condition: resourceValidationEntries.data?.compound[key].condition ?? '',
                    })) ?? [];
            const childRows: Array<Row> =
                Object.keys(resourceValidationEntries.data.child)
                    .filter((key) =>
                        filterSubType ?
                            (resourceValidationEntries.data?.child[key].persistableTypeFilter?.includes(
                                filterSubType
                            ) ?? true)
                            :   true
                    )
                    .map((key) => ({
                        severity: resourceValidationEntries.data?.child[key].defaultSeverity,
                        type: 'Child',
                        property: '-',
                        displayName: resourceValidationEntries.data?.child[key].displayName ?? formatIdentifier(key),
                        description: resourceValidationEntries.data?.child[key].description ?? undefined,
                        condition: resourceValidationEntries.data?.child[key].condition ?? '',
                    })) ?? [];
            return [...propertyRows, ...compoundRows, ...childRows];
        }
        return [];
    };

    const propertyColumns = useMemo(
        (): Array<
            MantineColumn<{
                displayName: string;
                functional: boolean;
                updatable: boolean;
                description?: string;
                condition?: string;
            }>
        > => [
            {
                type: 'string',
                accessorKey: 'displayName',
                header: 'Property',
                Header: null,
                Cell: ({ row }) => (
                    <ControlledInputs.String nullable={false} readOnly={true} value={row.original.displayName} />
                ),
            },
            {
                type: 'boolean',
                accessorKey: 'functional',
                header: 'Functional',
                Header: null,
                Cell: ({ row }) =>
                    row.original.functional ?
                        <IconCheck color={'var(--mantine-primary-color-filled)'} />
                        :   <IconX color={getColorMantine(ValidationEntrySeverity.CRITICAL)} />,
            },
            {
                type: 'boolean',
                accessorKey: 'updatable',
                header: 'Updatable',
                Header: null,
                Cell: ({ row }) =>
                    row.original.updatable ?
                        <IconCheck color={'var(--mantine-primary-color-filled)'} />
                        :   <IconX color={getColorMantine(ValidationEntrySeverity.CRITICAL)} />,
            },
        ],
        []
    );

    const validationColumns = useMemo(
        (): Array<
            MantineColumn<{
                severity?: ValidationEntrySeverity;
                type: string;
                property: string;
                displayName: string;
                description?: string;
                condition?: string;
            }>
        > => [
            {
                type: 'string',
                accessorKey: 'severity',
                header: 'Severity',
                Header: null,
                Cell: ({ row }) => (
                    <Tooltip label={row.original.severity ?? ''}>
                        <IconExclamationCircle
                            style={{ marginLeft: 6 }}
                            color={getColorMantine(row.original.severity)}
                        />
                    </Tooltip>
                ),
                enableColumnActions: false,
                enableHiding: false,
            },
            {
                type: 'string',
                accessorKey: 'type',
                header: 'Type',
                Header: null,
                Cell: ({ row }) => (
                    <ControlledInputs.String nullable={false} readOnly={true} value={row.original.type} />
                ),
            },
            {
                type: 'string',
                accessorKey: 'property',
                header: 'Property',
                Header: null,
                Cell: ({ row }) => (
                    <ControlledInputs.String nullable={false} readOnly={true} value={row.original.property} />
                ),
            },
            {
                type: 'string',
                accessorKey: 'displayName',
                header: 'Display Name',
                Header: null,
                Cell: ({ row }) => (
                    <ControlledInputs.String nullable={false} readOnly={true} value={row.original.displayName} />
                ),
            },
        ],
        []
    );

    return (
        <Grid>
            {subTypes.data && hasSubTypes() && (
                <>
                    <Grid.Col span={9}>
                        <Text c="dimmed">Resource Information</Text>
                    </Grid.Col>
                    <Grid.Col span={3}>
                        <ControlledInputs.Select
                            label={'Sub Type Filter'}
                            value={filterSubType}
                            nullable={true}
                            readOnly={false}
                            onChange={setFilterSubType}
                            data={subTypes.data}
                        />
                    </Grid.Col>
                </>
            )}
            {resourceProperties && (
                <Grid.Col span={12}>
                    <MantineTable
                        columns={propertyColumns}
                        data={getResourcePropertyRows()}
                        label={'Properties'}
                        tableOptions={{
                            enableBottomToolbar: false,
                            enablePagination: false,
                            renderDetailPanel: ({ row }) =>
                                getExpandedContent(row.original.description, row.original.condition),
                        }}
                        enableActions={false}
                    />
                </Grid.Col>
            )}
            {resourceValidationEntries.data && (
                <Grid.Col span={12}>
                    <MantineTable
                        columns={validationColumns}
                        data={getValidationEntryRows()}
                        label={'Possible Validation Entries'}
                        tableOptions={{
                            enableBottomToolbar: false,
                            enablePagination: false,
                            renderDetailPanel: ({ row }) =>
                                getExpandedContent(row.original.description, row.original.condition),
                        }}
                        enableActions={false}
                    />
                </Grid.Col>
            )}
        </Grid>
    );
};
